/**
 * Withholds the sentences of finished LinkedIn copy that a claims check flagged
 * and the regeneration budget could not repair.
 *
 * Round 5 (ITC Tax Brief) shipped a draft ad carrying two sentences the
 * judge itself named as unsupported ("Your timeline and long-term valuation
 * models are in limbo", "Capitalizing pre-construction assets … overstates
 * liability"), behind a needs_review footnote. A footnote does not stop an
 * approver: the sentence goes, and the ad ships without it or not at all.
 *
 * Pure. No I/O.
 */

/** The shortest a commentary may be after removals and still be an ad. */
export const MIN_COMMENTARY_CHARS_AFTER_REMOVAL = 80;

export interface WithholdableCopy {
  readonly commentary: string;
  readonly headline: string;
  readonly description?: string;
}

export interface FlaggedSentence {
  readonly sentence: string;
  readonly field?: string;
}

export interface WithholdResult {
  readonly commentary: string;
  readonly description: string | undefined;
  /** Every flagged sentence that was found and taken out. */
  readonly removed: string[];
  /** Flagged sentences that live in a field that cannot lose a sentence (the headline). */
  readonly unremovable: string[];
  /** Flagged sentences that could not be located in any field. */
  readonly notFound: string[];
  /** True when what is left of the commentary is too thin to be an ad. */
  readonly commentaryTooThin: boolean;
}

function normalise(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function sentencesOfParagraph(paragraph: string): string[] {
  return paragraph.split(/(?<=[.!?]["”’']?)\s+(?=[A-Z0-9"'“‘(])/u).filter(Boolean);
}

function matches(candidate: string, flagged: string): boolean {
  const a = normalise(candidate);
  const b = normalise(flagged);
  if (a.length < 12 || b.length < 12) return false;
  return a.includes(b) || b.includes(a);
}

/**
 * A flagged parenthetical ("(firm-wide report; results vary).") that sits INSIDE a longer sentence
 * is taken out of that sentence alone. Removing the whole sentence threw away the strongest line in
 * the ad: ITC Tax run 5, the judge quoted the caveat the writer had tacked onto "ITC Tax reports
 * $5M in property tax savings for a Texas data center client", and the $5M went with it.
 */
function withoutParenthetical(sentence: string, flag: string): string | null {
  const trimmed = flag.trim().replace(/[.!?]+$/, '');
  if (!trimmed.startsWith('(') || !trimmed.endsWith(')')) return null;
  const index = sentence.toLowerCase().indexOf(trimmed.toLowerCase());
  if (index <= 0) return null;
  const rest = (sentence.slice(0, index) + sentence.slice(index + trimmed.length))
    .replace(/\s+([.,;:!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (normalise(rest).length < 12 || normalise(rest) === normalise(sentence)) return null;
  return /[.!?]["”’']?$/.test(rest) ? rest : `${rest}.`;
}

const FIGURE = /[$€£¥]|\d|%|#/;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * A claim a check named is a phrase ("Now $58.00", "50%", "#1"), often shorter than the 12 characters
 * `matches` needs. A phrase holding a figure matches the raw text literally, with number boundaries and
 * the currency symbol kept: "$58" is not in "$580", "$58.50" or "$1.58", "$5,000" is not in "$5,000,000",
 * and "50%" is not in "50 courts". A phrase of words matches as whole words.
 */
function holdsPhrase(sentence: string, phrase: string): boolean {
  const literal = phrase.trim();
  if (FIGURE.test(literal)) {
    const body = literal.split(/\s+/).map(escapeRegExp).join('\\s+');
    const first = literal.charAt(0);
    const last = literal.charAt(literal.length - 1);
    const before = /\d/.test(first)
      ? '(?<![\\d.,])'
      : /[$€£¥#]/.test(first)
        ? '(?<![\\w$€£¥#])'
        : '(?<!\\w)';
    const after = /\d/.test(last) ? '(?!\\d|[.,]\\d)' : '(?!\\w)';
    return new RegExp(`${before}${body}${after}`, 'iu').test(sentence);
  }
  const a = normalise(sentence);
  const b = normalise(phrase);
  if (b.length < 2) return false;
  return ` ${a} `.includes(` ${b} `);
}

/** Splits before an emoji or a non-ASCII capital as well, so a sentence is never swallowed with its neighbour. */
function phraseSentencesOfParagraph(paragraph: string): string[] {
  return paragraph
    .split(/(?<=[.!?]["”’']?)\s+(?=[A-Z0-9"'“‘(\p{Lu}\p{Extended_Pictographic}])/u)
    .filter(Boolean);
}

/** A field left as one short word, or a lone abbreviation such as "Dr.", is a fragment, not copy. */
function isFragment(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return false;
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < 2) return true;
  return (
    /^(?:dr|mr|mrs|ms|prof|st|vs|no|inc|ltd|co|etc)\.$/i.test(words[words.length - 1]) &&
    words.length < 3
  );
}

function withoutFlagged(
  text: string,
  flagged: readonly string[],
  removed: Set<string>,
  phrases = false,
  hits: Map<string, number> = new Map(),
): string {
  const matchOf = phrases ? holdsPhrase : matches;
  const split = phrases ? phraseSentencesOfParagraph : sentencesOfParagraph;
  const parts = text.split(/(\n+)/);
  const items: { paragraph: string; separator: string }[] = [];
  for (let index = 0; index < parts.length; index += 2) {
    const paragraph = split(parts[index] ?? '')
      .flatMap((sentence) => {
        const hit = flagged.find((flag) => matchOf(sentence, flag));
        if (!hit) return [sentence];
        removed.add(hit);
        if (phrases) {
          flagged
            .filter((flag) => matchOf(sentence, flag))
            .forEach((flag) => {
              removed.add(flag);
              hits.set(flag, (hits.get(flag) ?? 0) + 1);
            });
        }
        const trimmed = withoutParenthetical(sentence, hit);
        return trimmed ? [trimmed] : [];
      })
      .join(' ')
      .trim();
    items.push({ paragraph, separator: parts[index + 1] ?? '' });
  }
  const kept = items.filter((item) => item.paragraph);
  return kept
    .map((item, index) => item.paragraph + (index < kept.length - 1 ? item.separator || '\n' : ''))
    .join('');
}

export function withholdFlaggedSentences(
  copy: WithholdableCopy,
  flagged: readonly FlaggedSentence[],
): WithholdResult {
  const sentences = [...new Set(flagged.map((item) => item.sentence.trim()).filter(Boolean))];
  const removed = new Set<string>();

  const commentary = withoutFlagged(copy.commentary, sentences, removed);
  const description = copy.description
    ? withoutFlagged(copy.description, sentences, removed) || undefined
    : undefined;

  const unremovable = sentences.filter(
    (sentence) => !removed.has(sentence) && matches(copy.headline, sentence),
  );
  const notFound = sentences.filter(
    (sentence) => !removed.has(sentence) && !unremovable.includes(sentence),
  );

  return {
    commentary,
    description,
    removed: [...removed],
    unremovable,
    notFound,
    commentaryTooThin:
      removed.size > 0 &&
      commentary.replace(/\s+/g, ' ').length < MIN_COMMENTARY_CHARS_AFTER_REMOVAL,
  };
}

export interface PhraseRemoval {
  /** The text with every sentence that holds a flagged phrase taken out. No word is added. */
  readonly text: string;
  /** The flagged phrases that were found in this text. */
  readonly removed: string[];
  /**
   * Set when the removal is not safe to offer: it would leave the field as a lone abbreviation or
   * fragment (`fragment`), or take more than one sentence for a single claim (`several`).
   */
  readonly unsafe?: 'fragment' | 'several';
}

/**
 * The same sentence removal `withholdFlaggedSentences` does, for one field and for the figures and
 * phrases a claims check names. A field that holds no flagged phrase comes back as it was. Existing
 * callers of this file are untouched: the number-aware match and the finer sentence split run only here.
 */
export function withoutFlaggedPhrases(text: string, phrases: readonly string[]): PhraseRemoval {
  const wanted = [...new Set(phrases.map((phrase) => phrase.trim()).filter(Boolean))];
  const removed = new Set<string>();
  const hits = new Map<string, number>();
  const result = withoutFlagged(text, wanted, removed, true, hits);
  if (removed.size === 0) return { text, removed: [] };
  const base = { text: result, removed: [...removed] };
  if ([...hits.values()].some((count) => count > 1)) return { ...base, unsafe: 'several' };
  if (isFragment(result)) return { ...base, unsafe: 'fragment' };
  return base;
}

/** True when the text still holds the phrase (figures literally, words as whole words). */
export function holdsFlaggedPhrase(text: string, phrase: string): boolean {
  return holdsPhrase(text, phrase);
}
